import { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Box,
  Text,
  Badge,
  VStack,
  HStack,
  Progress,
  Divider,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Icon,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { formatNumber, formatPercent } from '../../utils/format';
import { analyticsApi } from '../../api';

export const CampaignStatsModal = ({ isOpen, onClose, campaign }) => {
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const [zoneBreakdown, setZoneBreakdown] = useState([]);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);

  useEffect(() => {
    if (isOpen && campaign) {
      fetchZoneBreakdown();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, campaign?.id]);

  const fetchZoneBreakdown = async () => {
    if (!campaign) return;
    setLoadingBreakdown(true);
    try {
      // Fetch all-time stats to match the campaign card totals
      const result = await analyticsApi.getReportsByCampaignDetailed({
        start: new Date('2020-01-01').toISOString(),
        end: new Date().toISOString(),
      });
      const campaignData = result.data?.find(c => c.id === campaign.id || c.id === campaign.campaign_id);
      setZoneBreakdown(campaignData?.zones || []);
    } catch (err) {
      console.error('Failed to fetch zone breakdown:', err);
      setZoneBreakdown([]);
    } finally {
      setLoadingBreakdown(false);
    }
  };

  if (!campaign) return null;

  const impressions = campaign.impressions || 0;
  const clicks = campaign.clicks || 0;
  const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
  const totalLimit = campaign.total_impressions_limit;
  const progress = totalLimit ? (impressions / totalLimit) * 100 : 0;

  const formatDate = (date) => {
    if (!date) return 'Not set';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="2xl" mx={4}>
        <ModalHeader>
          <HStack justify="space-between" align="start" pr={8}>
            <VStack align="start" spacing={1}>
              <Text>{campaign.campaign_name || campaign.name}</Text>
              <Text fontSize="sm" fontWeight="normal" color="gray.500">
                {campaign.advertiser_name || campaign.advertiser}
              </Text>
            </VStack>
            <Badge
              colorScheme={campaign.status === 'active' ? 'green' : campaign.status === 'paused' ? 'yellow' : 'gray'}
              borderRadius="full"
              px={3}
              py={1}
            >
              {campaign.status}
            </Badge>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={6} align="stretch">
            {/* Main Stats */}
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
              <Box bg={cardBg} p={4} borderRadius="xl">
                <Stat>
                  <StatLabel color="gray.500">Impressions</StatLabel>
                  <StatNumber fontSize="2xl">{formatNumber(impressions)}</StatNumber>
                  {totalLimit && (
                    <StatHelpText mb={0}>of {formatNumber(totalLimit)}</StatHelpText>
                  )}
                </Stat>
              </Box>
              <Box bg={cardBg} p={4} borderRadius="xl">
                <Stat>
                  <StatLabel color="gray.500">Clicks</StatLabel>
                  <StatNumber fontSize="2xl">{formatNumber(clicks)}</StatNumber>
                </Stat>
              </Box>
              <Box bg={cardBg} p={4} borderRadius="xl">
                <Stat>
                  <StatLabel color="gray.500">CTR</StatLabel>
                  <StatNumber fontSize="2xl">{formatPercent(ctr, 2)}</StatNumber>
                  <StatHelpText mb={0}>
                    <StatArrow type={ctr >= 2 ? 'increase' : 'decrease'} />
                    {ctr >= 2 ? 'Good' : 'Below avg'}
                  </StatHelpText>
                </Stat>
              </Box>
              <Box bg={cardBg} p={4} borderRadius="xl">
                <Stat>
                  <StatLabel color="gray.500">Priority</StatLabel>
                  <StatNumber fontSize="2xl">{campaign.priority || 5}</StatNumber>
                  <StatHelpText mb={0}>Weight: {campaign.weight || 1}</StatHelpText>
                </Stat>
              </Box>
            </SimpleGrid>

            {/* Progress to limit */}
            {totalLimit && (
              <Box>
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="sm" fontWeight="500">Impressions Progress</Text>
                  <Text fontSize="sm" color="gray.500">{formatPercent(progress, 1)}</Text>
                </HStack>
                <Progress
                  value={progress}
                  colorScheme={progress >= 90 ? 'red' : progress >= 70 ? 'yellow' : 'brand'}
                  borderRadius="full"
                  size="lg"
                />
              </Box>
            )}

            <Divider />

            {/* Zone Breakdown */}
            <Box>
              <HStack mb={3}>
                <Icon color="purple.500" boxSize={4}>
                  <FontAwesomeIcon icon={faLayerGroup} />
                </Icon>
                <Text fontWeight="600">Stats by Zone</Text>
              </HStack>
              {loadingBreakdown ? (
                <Box textAlign="center" py={4}>
                  <Spinner size="sm" color="brand.500" />
                  <Text fontSize="sm" color="gray.500" mt={2}>Loading breakdown...</Text>
                </Box>
              ) : zoneBreakdown.length > 0 ? (
                <Box border="1px solid" borderColor={borderColor} borderRadius="xl" overflow="hidden">
                  <TableContainer>
                    <Table size="sm">
                      <Thead bg={cardBg}>
                        <Tr>
                          <Th>Zone</Th>
                          <Th>Site</Th>
                          <Th isNumeric>Impressions</Th>
                          <Th isNumeric>Clicks</Th>
                          <Th isNumeric>CTR</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {zoneBreakdown.map((zone) => (
                          <Tr key={zone.id}>
                            <Td fontWeight="500">{zone.name}</Td>
                            <Td color="gray.500" fontSize="sm">{zone.site}</Td>
                            <Td isNumeric>{formatNumber(zone.impressions)}</Td>
                            <Td isNumeric>{formatNumber(zone.clicks)}</Td>
                            <Td isNumeric>
                              <Badge colorScheme="purple" variant="subtle" borderRadius="full">
                                {zone.ctr}%
                              </Badge>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              ) : (
                <Box bg={cardBg} p={4} borderRadius="xl" textAlign="center">
                  <Text color="gray.500" fontSize="sm">No zone breakdown data available</Text>
                </Box>
              )}
            </Box>

            <Divider />

            {/* Campaign Details */}
            <SimpleGrid columns={2} spacing={4}>
              <Box>
                <Text fontSize="sm" color="gray.500" mb={1}>Start Date</Text>
                <Text fontWeight="500">{formatDate(campaign.start_date)}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500" mb={1}>End Date</Text>
                <Text fontWeight="500">{formatDate(campaign.end_date)}</Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500" mb={1}>Daily Limit</Text>
                <Text fontWeight="500">
                  {campaign.daily_impressions_limit
                    ? formatNumber(campaign.daily_impressions_limit)
                    : 'Unlimited'}
                </Text>
              </Box>
              <Box>
                <Text fontSize="sm" color="gray.500" mb={1}>Created</Text>
                <Text fontWeight="500">{formatDate(campaign.created_at)}</Text>
              </Box>
            </SimpleGrid>

            {campaign.description && (
              <>
                <Divider />
                <Box>
                  <Text fontSize="sm" color="gray.500" mb={1}>Description</Text>
                  <Text>{campaign.description}</Text>
                </Box>
              </>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose} borderRadius="xl">
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CampaignStatsModal;
